import React, { useEffect, useRef, useState } from "react";

import { CpSelect } from "../cp-select/cp-select.component";
import { CpInput } from "@components";

export type TimeValue = {
  hour: number;
  minute: number;
  formattedTime: string;
};

type CpTimeProps = {
  className?: string;
  contentWidth?: string;
  clearable?: boolean;
  disabled?: boolean;
  onChange: (value: TimeValue | null) => void;
  position?: string; // TODO: Type this properly once CpDropdown is migrated
  triggerIsBlock?: boolean;
  value?: TimeValue | null;
  minTime?: Omit<TimeValue, "formattedTime">;
};

export function CpTime({
  className,
  contentWidth,
  clearable,
  disabled,
  onChange,
  position,
  triggerIsBlock,
  value,
  minTime,
}: CpTimeProps) {
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current.value = value?.formattedTime || "";
    }
  }, [value]);

  function onInputChange(searchOnChange: (value: string) => void) {
    const inputVal = inputRef?.current?.value || "";
    searchOnChange(inputVal);
  }

  function selectOnChange(val: TimeValue | null) {
    if (inputRef?.current) {
      inputRef.current.value = val?.formattedTime || "";
    }
    onChange(val);
  }

  function settle() {
    const inputVal = inputRef?.current?.value;
    const isNewVal = inputVal !== value?.formattedTime;
    if (!isNewVal) return;
    if (!inputVal) {
      selectOnChange(null);
      return;
    }

    const builtTime = buildTime(inputVal);
    if (isBeforeMinTime(builtTime)) {
      selectOnChange({
        ...minTime!,
        formattedTime: formatTime(minTime!.hour, minTime!.minute),
      });
    } else {
      selectOnChange(builtTime);
    }
  }

  function selectAll() {
    if (inputRef?.current) {
      inputRef.current.select();
    }
  }

  function isBeforeMinTime(time: TimeValue | null): boolean {
    if (!minTime || !time) return false;
    return (
      time.hour < minTime.hour ||
      (time.hour === minTime.hour && time.minute < minTime.minute)
    );
  }

  return (
    <CpSelect
      contentWidth={contentWidth}
      className={className}
      data={
        minTime ? timeList.filter((time) => !isBeforeMinTime(time)) : timeList
      }
      disabled={disabled}
      position={position}
      onChange={selectOnChange}
      renderItem={(CpSelect as any).ItemSingle}
      renderTrigger={({ open, searchOnChange, close }: any) => (
        <CpInput
          onClick={open}
          onChange={() => onInputChange(searchOnChange)}
          placeholder="Select time"
          autoFocus
          onFocus={selectAll}
          onBlur={settle}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
              close();
              settle();
            }
          }}
          disabled={disabled}
          ref={inputRef}
        />
      )}
      searchFilter={
        (CpSelect as any).filterAlpha(search) as (item: any) => boolean
      }
      searchHideEmptyState
      searchOnChange={setSearch}
      searchValue={search}
      transformData={(item: TimeValue) => ({
        id: item.formattedTime,
        name: item.formattedTime,
      })}
      triggerIsBlock={triggerIsBlock}
      value={value}
      clearable={clearable}
      {...({} as any)} // TODO: Remove when CpSelect is migrated to TypeScript
    />
  );
}

const regexHourMinute = /([0-9]?[0-9]):([0-9]?[0-9])(am|pm)?/i;
const regexHour = /([0-9]?[0-9])(am|pm)?/i;

const hours = Array.from(Array(24), (_, i) => i);
const minutes = [0, 30];

export const timeList: TimeValue[] = hours.reduce((acc: TimeValue[], hour) => {
  return [
    ...acc,
    ...minutes.map((minute) => ({
      hour,
      minute,
      formattedTime: formatTime(hour, minute),
    })),
  ];
}, []);

function formatTime(hour: number, minute: number): string {
  let hr;
  if (hour === 0) hr = 12;
  else if (hour > 0 && hour < 12) hr = hour;
  else if (hour > 12) hr = hour - 12;
  else hr = hour;
  const mer = hour > 11 ? "pm" : "am";
  return `${hr}:${minute.toString().padStart(2, "0")}${mer}`;
}

function buildTime(str: string): TimeValue | null {
  if (!str) return null;
  let hour: string | undefined,
    minute: string | undefined,
    meridiem: string | undefined;
  const hourMinuteResult = str.match(regexHourMinute);
  const hourResult = str.match(regexHour);
  if (hourMinuteResult) {
    [, hour, minute, meridiem] = hourMinuteResult;
  } else if (hourResult) {
    [, hour, meridiem] = hourResult;
  }

  if (!hour && !minute && !meridiem) return null;

  let hourNumber = parseInt(hour || "0");
  let minuteNumber = parseInt(minute || "0");

  if (!minuteNumber) {
    minuteNumber = 0;
  } else if (minute && minute[0] !== "0" && minuteNumber < 6) {
    minuteNumber = minuteNumber * 10;
  } else if (minuteNumber > 59) {
    minuteNumber = 59;
  }

  if (!hourNumber) {
    hourNumber = 0;
  } else if (hourNumber > 23) {
    hourNumber = 23;
  } else if (hourNumber === 12 && meridiem === "am") {
    hourNumber = 0;
  } else if (hourNumber < 12 && meridiem === "pm") {
    hourNumber = hourNumber + 12;
  }
  return {
    hour: hourNumber,
    minute: minuteNumber,
    formattedTime: formatTime(hourNumber, minuteNumber),
  };
}
